/**
 * PORT (wide screens): the battle's result popups (victory, defeat, a new level, the weapons it opened, a surprise, the
 * graves' search and what a grave held, the messages that nothing opened or is left to search, an online battle's
 * result) drawn larger on a phone, laid out by the wide screen (ui/wide-screen.ts, ScreenDress.custom). The original
 * drew them for its 807x600 window: 11-12 px text, a close button of 28x30 px, buttons 27-40 px tall; on a phone in
 * landscape (0.53-0.69 CSS px per px) that is 6-8 CSS px text and 19-27 CSS px buttons. With a wide host the popup is
 * scaled about its middle, in the middle of the shown safe area, until its text measures POPUP_TEXT_CSS CSS px (never
 * smaller than the original, at most as large as the shown area holds it), and every button of it takes taps on at
 * least HIT_CSS CSS px square (a hit area of its own around its art, or around the hit area of an invisible button: a
 * grave's row; clear of the other buttons', on the screen). Its slide in and out (FWP._main sets its y) goes on under
 * the scale. Drawing and hit areas only: the popups' texts, buttons and clicks are the game's.
 */
import { Point, Sprite } from '@flash';
import { type Piece, type ScreenContext, type ScreenLayout, pieceOf } from './battle-sheet';

/** A rectangle in the popup's coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The popups' text aimed at (CSS px) and the smallest text they have (px as the original draws it). */
export const POPUP_TEXT_CSS = 13;
const POPUP_TEXT_PX = 11;
/** The touch target a popup's buttons get at least (CSS px). */
export const HIT_CSS = 48;
/** The room the popup leaves to the shown area's edges (its share of it). */
const FIT = 0.94;

interface PopupState {
  on: boolean;
  /** The popup's box at scale 1 and its scale now. */
  box: Area | null;
  k: number;
  /** Where the game last placed it (its y: the slide) and where the layout put it. */
  gameX: number;
  gameY: number;
  shownX: number;
  shownY: number;
  /** The buttons given hit areas: their own hit state as the original has it. */
  hits: Map<any, any>;
  hitKey: string;
}

function stateOf(root: any): PopupState {
  if (!root.$popup) root.$popup = { on: false, box: null, k: 1, gameX: root.x, gameY: root.y, shownX: root.x, shownY: root.y, hits: new Map(), hitKey: '' } as PopupState;
  return root.$popup as PopupState;
}

/** The popup's art at scale 1 (its shown children, of a list only the rows shown; the port's parts not counted). */
function boxOf(root: any): Area | null {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const add = (c: any, depth: number) => {
    if (!c.visible || String(c.name).startsWith('$wide_')) return;
    // (a container of rows, the graves' list: its rows shown, not the room of the hidden ones)
    if (depth < 1 && c.upState === undefined && typeof c.numChildren === 'number' && c.numChildren > 1 && c.name && /_container$/.test(String(c.name))) {
      for (let i = 0; i < c.numChildren; i++) add(c.getChildAt(i), depth + 1);
      return;
    }
    const b = c.getBounds(root);
    if (!(b.width > 0 && b.height > 0)) return;
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width);
    y1 = Math.max(y1, b.y + b.height);
  };
  for (let i = 0; i < root.numChildren; i++) add(root.getChildAt(i), 0);
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } : null;
}

/**
 * What the battle's popup `root` shows now, in the coordinates of `space` (the stage): its art as boxOf counts it (the
 * rows of a list shown, not the room of the hidden ones), not its bounds, which count its hidden objects too.
 */
export function shownRect(root: any, space: any): Area | null {
  const b = boxOf(root);
  if (!b) return null;
  const p0 = space.globalToLocal(root.localToGlobal(new Point(b.x, b.y)));
  const p1 = space.globalToLocal(root.localToGlobal(new Point(b.x + b.width, b.y + b.height)));
  return { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), width: Math.abs(p1.x - p0.x), height: Math.abs(p1.y - p0.y) };
}

/** The popup's buttons (SimpleButtons among its children and their children), shown and taking the mouse. */
function buttonsOf(root: any): any[] {
  const out: any[] = [];
  const walk = (o: any, depth: number) => {
    for (let i = 0; i < o.numChildren; i++) {
      const c = o.getChildAt(i);
      if (!c.visible || String(c.name).startsWith('$wide_')) continue;
      if (c.hitTestState !== undefined && c.upState !== undefined) {
        if (c.mouseEnabled !== false && c.enabled !== false) out.push(c);
      } else if (depth < 3 && typeof c.numChildren === 'number' && c.mouseChildren !== false) walk(c, depth + 1);
    }
  };
  walk(root, 0);
  return out;
}

/**
 * A button's place in the popup's coordinates: its art, or (an invisible button: a row of a list, the graves' search)
 * the hit area the original gave it.
 */
function buttonArea(b: any, root: any): Area {
  const r = b.getBounds(root);
  if (r.width > 0 && r.height > 0) return { x: r.x, y: r.y, width: r.width, height: r.height };
  const h = b.hitTestState;
  const q = h && typeof h.getBounds === 'function' ? h.getBounds(root) : r;
  return { x: q.x, y: q.y, width: q.width, height: q.height };
}

/**
 * Hit areas of at least `side` px square (the popup's coordinates) round each button's art, clear of the others' and
 * on the screen (`screen`: the shown safe area, the stage's coordinates; beyond the popup's art a tap would only reach
 * the dim under it); a button whose own hit area is that large already keeps it.
 */
function setHits(st: PopupState, root: any, side: number | null, screen: Area | null = null): void {
  const buttons = side ? buttonsOf(root) : [];
  // (again when the buttons, their places or their art changed: the graves' rows laid out anew, a grave searched)
  const at = (b: any) => {
    const p = root.globalToLocal(b.localToGlobal(new Point(0, 0)));
    const q = b.getBounds(root);
    return `${b.name}:${p.x.toFixed(1)},${p.y.toFixed(1)},${q.width.toFixed(1)},${q.height.toFixed(1)}`;
  };
  const key = side ? `${side.toFixed(2)}|${screen ? [screen.x, screen.y, screen.width, screen.height].map(Math.round).join(',') : ''}|${buttons.map(at).join(';')}` : '';
  if (key === st.hitKey) return;
  st.hitKey = key;
  for (const [b, orig] of st.hits) b.hitTestState = orig;
  st.hits.clear();
  if (!side) return;
  const arts = buttons.map((b) => ({ b, r: buttonArea(b, root) }));
  // The screen in the popup's coordinates.
  let bounds: Area | null = null;
  if (screen) {
    const p0 = root.globalToLocal(new Point(screen.x, screen.y));
    const p1 = root.globalToLocal(new Point(screen.x + screen.width, screen.y + screen.height));
    bounds = { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), width: Math.abs(p1.x - p0.x), height: Math.abs(p1.y - p0.y) };
  }
  for (const a of arts) {
    const r = a.r;
    const h0 = a.b.hitTestState;
    const hb = h0 && typeof h0.getBounds === 'function' ? h0.getBounds(root) : null;
    if ((r.width >= side && r.height >= side) || (hb && hb.width >= side && hb.height >= side)) continue;
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    let x0 = Math.min(r.x, cx - side / 2);
    let x1 = Math.max(r.x + r.width, cx + side / 2);
    let y0 = Math.min(r.y, cy - side / 2);
    let y1 = Math.max(r.y + r.height, cy + side / 2);
    // Clear of the other buttons' art: up to the middle of the gap between them.
    for (const o of arts) {
      if (o === a) continue;
      const q = o.r;
      const overlapY = y0 < q.y + q.height && q.y < y1;
      const overlapX = x0 < q.x + q.width && q.x < x1;
      if (overlapY && q.x >= r.x + r.width) x1 = Math.min(x1, (r.x + r.width + q.x) / 2);
      if (overlapY && q.x + q.width <= r.x) x0 = Math.max(x0, (q.x + q.width + r.x) / 2);
      if (overlapX && q.y >= r.y + r.height) y1 = Math.min(y1, (r.y + r.height + q.y) / 2);
      if (overlapX && q.y + q.height <= r.y) y0 = Math.max(y0, (q.y + q.height + r.y) / 2);
    }
    // On the screen, never smaller than the art.
    if (bounds) {
      x0 = Math.max(x0, Math.min(r.x, bounds.x));
      y0 = Math.max(y0, Math.min(r.y, bounds.y));
      x1 = Math.min(x1, Math.max(r.x + r.width, bounds.x + bounds.width));
      y1 = Math.min(y1, Math.max(r.y + r.height, bounds.y + bounds.height));
    }
    // The hit state in the button's own coordinates.
    const p0 = a.b.globalToLocal(root.localToGlobal(new Point(x0, y0)));
    const p1 = a.b.globalToLocal(root.localToGlobal(new Point(x1, y1)));
    const hit = new Sprite();
    hit.graphics.beginFill(0, 0);
    hit.graphics.drawRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y));
    hit.graphics.endFill();
    st.hits.set(a.b, a.b.hitTestState);
    a.b.hitTestState = hit;
  }
}

/** A result popup's layout; `when`: only for the screens it holds for (the question popup in its online result mode). */
export function resultPopup(when?: (fwp: any) => boolean): ScreenLayout {
  const place = (root: any, ctx: ScreenContext) => {
    const st = stateOf(root);
    const { stage, fwp } = ctx;
    // (the game moved it: its slide; positions are kept in twips, read back rounded)
    if (Math.abs(root.x - st.shownX) > 0.1) st.gameX = root.x;
    if (Math.abs(root.y - st.shownY) > 0.1) st.gameY = root.y;
    const on = !!stage && !!stage.$visibleArea && (!when || when(fwp));
    if (!on) {
      if (st.on) {
        root.scaleX = root.scaleY = 1;
        root.x = st.gameX;
        root.y = st.gameY;
        st.shownX = root.x;
        st.shownY = root.y;
        setHits(st, root, null);
      }
      st.on = false;
      return;
    }
    const css = stage.cssScale > 0 ? stage.cssScale : 1;
    const V = stage.safeRect;
    const box = (st.box ??= boxOf(root));
    if (!box) return;
    const k = Math.max(1, Math.min(POPUP_TEXT_CSS / (POPUP_TEXT_PX * css), (FIT * V.width) / box.width, (FIT * V.height) / box.height));
    st.on = true;
    st.k = k;
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    root.scaleX = root.scaleY = k;
    root.x = st.gameX + V.x + V.width / 2 - cx * k;
    root.y = st.gameY + V.y + V.height / 2 - cy * k;
    st.shownX = root.x;
    st.shownY = root.y;
    setHits(st, root, HIT_CSS / (css * k), V);
  };
  return {
    layout: place,
    frame(root, ctx) {
      const st = stateOf(root);
      // (again every frame while on: the slide, and buttons the game shows later, an ad's bonus: their hit areas)
      if (st.on || Math.abs(root.x - st.shownX) > 0.1 || Math.abs(root.y - st.shownY) > 0.1) place(root, ctx);
    },
  };
}

/** The result popups of a battle (their linkage classes). */
export const RESULT_POPUP = resultPopup();

/** The graves' search (menu_grave_search): its frame (a flat panel), the sticks at its lower left, the plant at its
 * upper right, its list of graves (clips 200 px apart, rows 40 px apart). */
const GRAVE_FRAME = 839;
const GRAVE_STICKS = 67;
const GRAVE_PLANT = 65;
const GRAVE_COL = 200;
const GRAVE_ROW = 40;
/** The frame's part under its list that is kept whole (its border and the room under the last row). */
const GRAVE_FOOT = 60;
/** The frame's bottom under its last row: the row's grave (20 px lower than the row) and the sticks under it. */
const GRAVE_UNDER = 74;
/** The frame's side parts kept whole (its border and the room inside it). */
const GRAVE_EDGE = 36;
/** The height of the text above the graves in the original (its field: the list starts right under it). */
const GRAVE_TEXT_H = 87;

type GravePart = 'tl' | 'tm' | 'tr' | 'bl' | 'bm' | 'br';

interface GraveState {
  key: string;
  frame: any;
  parts: Record<GravePart, Piece | null>;
  /** The children the layout moves, as the original has them. */
  orig: Map<any, { x: number; y: number; width: number | null }>;
}

/**
 * The graves' search: the original's frame holds 16 graves in 8 rows of 2 (a popup 477 px tall that a phone could not
 * show larger). With a wide host it is laid out for the graves shown: in 2, 3 or 4 columns (the frame widened: its sides
 * moved out, its flat body stretched between; the text above the graves in fewer lines), as tall as their rows need (the
 * frame's foot moved up, with the sticks at its corner), whichever lets the popup be scaled the largest; then scaled as
 * the other results. False when nothing changed.
 */
function arrangeGraves(root: any, stage: any, on: boolean): boolean {
  if (!root.$graves) {
    const frame = (() => {
      for (let i = 0; i < root.numChildren; i++) if (root.getChildAt(i).$charId === GRAVE_FRAME) return root.getChildAt(i);
      return null;
    })();
    const parts = {} as Record<GravePart, Piece | null>;
    let at = frame ? root.getChildIndex(frame) + 1 : 0;
    // (the stretched middle first: the sides are drawn over its ends)
    for (const k of ['tm', 'bm', 'tl', 'tr', 'bl', 'br'] as GravePart[]) {
      parts[k] = frame ? pieceOf(frame, '$grave_' + k) : null;
      if (parts[k]) root.addChildAt(parts[k]!.sprite, at++);
    }
    root.$graves = { key: '', frame, parts, orig: new Map() } as GraveState;
  }
  const st = root.$graves as GraveState;
  const cont = root.grave_search_container;
  const find = (test: (c: any) => boolean) => {
    for (let i = 0; i < root.numChildren; i++) if (test(root.getChildAt(i))) return root.getChildAt(i);
    return null;
  };
  const text = find((c) => typeof c.textWidth === 'number' && c.$charId === 840);
  const title = find((c) => typeof c.textWidth === 'number' && c.$charId === 841);
  const sticks = find((c) => c.$charId === GRAVE_STICKS);
  const plant = find((c) => c.$charId === GRAVE_PLANT);
  const close = root.close;
  const P = st.parts;
  if (!st.frame || !cont || !text || !title || Object.values(P).some((p) => !p)) return false;
  const moved = [cont, text, title, sticks, plant, close].filter(Boolean);
  for (const o of moved) if (!st.orig.has(o)) st.orig.set(o, { x: o.x, y: o.y, width: typeof o.textWidth === 'number' ? o.width : null });
  const cells: any[] = [];
  for (let i = 0; i < cont.numChildren; i++) cells.push(cont.getChildAt(i));
  const n = cells.filter((c) => c.visible).length;
  const restore = () => {
    for (const [o, v] of st.orig) {
      o.x = v.x;
      o.y = v.y;
      if (v.width !== null) o.width = v.width;
    }
    cells.forEach((c, i) => {
      c.x = (i % 2) * GRAVE_COL;
      c.y = Math.floor(i / 2) * GRAVE_ROW;
    });
    st.frame.visible = true;
    for (const p of Object.values(P)) p!.place({ x: 0, y: 0, width: 1, height: 1 }, null);
  };
  if (!on) {
    if (st.key === '') return false;
    st.key = '';
    restore();
    return true;
  }
  // The frame as the original has it.
  st.frame.visible = true;
  const b = st.frame.getBounds(root);
  st.frame.visible = false;
  const x0 = b.x;
  const y0 = b.y;
  const x1 = b.x + b.width;
  const y1 = b.y + b.height;
  const ot = st.orig.get(text)!;
  const oc = st.orig.get(cont)!;
  const css = stage.cssScale > 0 ? stage.cssScale : 1;
  const V = stage.safeRect;
  const aim = POPUP_TEXT_CSS / (POPUP_TEXT_PX * css);
  // The layout for 2, 3 and 4 columns: the one scaled the largest (a column more only for a clear gain).
  let best: { cols: number; k: number; textH: number } | null = null;
  for (const cols of [2, 3, 4]) {
    if (cols > 2 && n <= (cols - 1) * 2) break; // (fewer graves than a row more of the narrower layout holds)
    const extra = (cols - 2) * GRAVE_COL;
    text.width = ot.width! + extra;
    const textH = text.textHeight + 4;
    const rows = Math.max(1, Math.ceil(n / cols));
    const h = oc.y - (GRAVE_TEXT_H - textH) + rows * GRAVE_ROW + GRAVE_UNDER - y0;
    const k = Math.min(aim, (0.94 * V.width) / (b.width + extra + 14), (0.94 * V.height) / h);
    if (!best || k > best.k * 1.02) best = { cols, k, textH };
  }
  const { cols, textH } = best!;
  const rows = Math.max(1, Math.ceil(n / cols));
  const key = `${cols}x${rows}|${n}`;
  if (key === st.key) {
    text.width = ot.width! + (cols - 2) * GRAVE_COL;
    return false;
  }
  st.key = key;
  const extra = (cols - 2) * GRAVE_COL;
  text.width = ot.width! + extra;
  title.width = st.orig.get(title)!.width! + extra;
  // The list under the text (as far below it as in the original: up by what the text is shorter than its field),
  // where it was in the frame (the frame is wider by its columns).
  const listY = oc.y - (GRAVE_TEXT_H - textH);
  cont.y = listY;
  cont.x = oc.x;
  cells.forEach((c, i) => {
    c.x = (i % cols) * GRAVE_COL;
    c.y = Math.floor(i / cols) * GRAVE_ROW;
  });
  const cut = Math.min(y1, listY + rows * GRAVE_ROW + GRAVE_UNDER);
  const X1 = x1 + extra;
  const E = GRAVE_EDGE;
  // (the top reaches 2 px under the foot, the middle 1 px under the sides: no line where they meet)
  const topH = cut - GRAVE_FOOT + 2 - y0;
  const src = (x: number, y: number, w: number, h: number) => ({ x, y, width: w, height: h });
  P.tl!.place(src(x0, y0, E, topH), src(x0, y0, E, topH));
  P.tr!.place(src(x1 - E, y0, E, topH), src(X1 - E, y0, E, topH));
  P.tm!.place(src(x0 + E + 4, y0, 1, topH), src(x0 + E - 1, y0, X1 - x0 - 2 * E + 2, topH));
  P.bl!.place(src(x0, y1 - GRAVE_FOOT, E, GRAVE_FOOT), src(x0, cut - GRAVE_FOOT, E, GRAVE_FOOT));
  P.br!.place(src(x1 - E, y1 - GRAVE_FOOT, E, GRAVE_FOOT), src(X1 - E, cut - GRAVE_FOOT, E, GRAVE_FOOT));
  P.bm!.place(src(x0 + E + 4, y1 - GRAVE_FOOT, 1, GRAVE_FOOT), src(x0 + E - 1, cut - GRAVE_FOOT, X1 - x0 - 2 * E + 2, GRAVE_FOOT));
  if (sticks) sticks.y = st.orig.get(sticks)!.y + cut - y1;
  if (plant) plant.x = st.orig.get(plant)!.x + extra;
  if (close) close.x = st.orig.get(close)!.x + extra;
  return true;
}

const GRAVES_POPUP = resultPopup();

/** The graves' search: as tall as its graves need, then scaled as the other results. */
export const GRAVE_SEARCH: ScreenLayout = {
  layout(root, ctx) {
    const on = !!ctx.stage && !!ctx.stage.$visibleArea;
    // (unscaled while the frame is measured and cut)
    if (arrangeGraves(root, ctx.stage, on)) stateOf(root).box = null;
    GRAVES_POPUP.layout(root, ctx);
  },
  frame(root, ctx) {
    if (arrangeGraves(root, ctx.stage, !!ctx.stage && !!ctx.stage.$visibleArea)) {
      stateOf(root).box = null;
      GRAVES_POPUP.layout(root, ctx);
    } else GRAVES_POPUP.frame!(root, ctx);
  },
};

/**
 * The message popup (menu_error) where the battle's results show it: the graves' search with nothing to search (all
 * drowned; an assault), the weapons a level opened when it opened none. Its other messages (the menus') keep the
 * original's popup.
 */
export const BATTLE_MESSAGES: readonly string[] = ['grave_search', 'grave_search_assault', 'what_opened'];
export const BATTLE_MESSAGE_POPUP = resultPopup((fwp) => !!fwp && !!fwp._interf && BATTLE_MESSAGES.includes(String(fwp._interf.error_mode)));

/** The question popup as an online battle's result (runtime/online-lobby.ts: menu_question, mode online_result). */
export const ONLINE_RESULT_POPUP = resultPopup((fwp) => !!fwp && !!fwp._interf && fwp._interf.question_mode == 'online_result');

/** The battle's popups this module and the weapon sheet / scanner lay out (ui/wide-screen.ts); ui/wide-popups.ts leaves them alone. */
// (a grave's find and the roulette's prize are menu_surprise too: RESULT_POPUP lays them out, in the menus as well)
const BATTLE_POPUPS = new Set(['weapon_sheet', 'scaner', 'end_game_win', 'end_game_lose', 'end_game_new_lvl', 'end_game_surprise', 'what_opened', 'surprise', 'grave_search', 'grave_result', 'roul result']);

/** Whether the battle's layer lays out the screen `name` (without the menu_ prefix) now: then it is not grown twice. */
export function battleOwnsPopup(fwp: any, name: string): boolean {
  if (BATTLE_POPUPS.has(name)) return true;
  const i = fwp && fwp._interf;
  if (name === 'error') return !!i && BATTLE_MESSAGES.includes(String(i.error_mode));
  if (name === 'question') return !!i && i.question_mode == 'online_result';
  return false;
}
